// QuizResults.tsx - Updated to receive props from parent
import { memo } from "react";
import { Certificate } from "./Certificate";
import { Award, CheckCircle2, XCircle, RotateCw, Printer } from "lucide-react";

interface QuizResultsProps {
  title: string;
  studentName: string;
  score: number;
  total: number;
  percentage: number;
  passed: boolean;
  primaryColor: string;
  onRestart: () => void;
  onPrint?: () => void;
  correctAnswers?: number;
  totalQuestions?: number;
  grade?: string;
  topicIcon?: string;
}

export const QuizResults = memo(({ 
  title, 
  studentName, 
  score, 
  total, 
  percentage, 
  passed, 
  primaryColor, 
  onRestart,
  onPrint,
  correctAnswers,
  totalQuestions,
  grade,
  topicIcon = "🏆",
}: QuizResultsProps) => {
  const getScoreColor = () => {
    if (percentage >= 85) return "text-green-600";
    if (percentage >= 70) return "text-blue-600";
    if (percentage >= 50) return "text-yellow-600";
    return "text-red-600";
  };

  const getGradeLevel = () => {
    if (percentage >= 90) return "🌟 Excellent!";
    if (percentage >= 80) return "⭐ Great Job!";
    if (percentage >= 70) return "👍 Good Work!";
    if (percentage >= 60) return "📖 Keep Practicing!";
    if (percentage >= 50) return "💪 Almost There!";
    return "🔄 Try Again!";
  };

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6">
      <div className="bg-white rounded-3xl shadow-xl border overflow-hidden">
        {/* Header */}
        <div className={`${primaryColor} text-white p-6 md:p-8 text-center`}>
          <div className="text-5xl mb-3">{passed ? "🎉" : "😅"}</div>
          <h1 className="text-2xl md:text-4xl font-bold mb-2">
            {passed ? "Quiz Completed!" : "Quiz Finished"}
          </h1>
          <p className="text-sm md:text-lg opacity-90">{title}</p>
          <p className="text-xs md:text-sm opacity-70 mt-1">{studentName}</p>
        </div>

        <div className="p-6 md:p-8 text-center">
          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mb-6">
            <StatCard 
              label="Score" 
              value={`${score}/${total}`} 
              icon={<Award className="w-4 h-4" />}
            />
            <StatCard 
              label="Percentage" 
              value={`${percentage}%`} 
              valueClassName={getScoreColor()}
              icon={<Award className="w-4 h-4" />}
            />
            <StatCard 
              label="Result" 
              value={passed ? "PASSED" : "FAILED"} 
              valueClassName={passed ? "text-green-600" : "text-red-600"}
              icon={passed ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
            />
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-3 mb-6">
            <div
              className={`h-3 rounded-full transition-all duration-1000 ${
                passed ? 'bg-green-500' : 'bg-yellow-500'
              }`}
              style={{ width: `${percentage}%` }}
            />
          </div>

          {/* Grade Level */}
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 ${
            passed ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
          }`}>
            <span className="text-lg">{passed ? "✅" : "📚"}</span>
            <span className="font-semibold">{getGradeLevel()}</span>
          </div>

          {/* Certificate */}
          {passed && (
            <div className="mb-6">
              <Certificate
                title={title}
                studentName={studentName}
                percentage={percentage}
                grade={grade}
                topicIcon={topicIcon}
                certificateId={`CERT-${Date.now().toString().slice(-6)}`}
                issueDate={new Date().toISOString()}
                passed={passed}
                correctAnswers={correctAnswers || score}
                totalQuestions={totalQuestions || total}
              />
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap items-center justify-center gap-3">
            {passed && (
              <button 
                onClick={onPrint || (() => window.print())} 
                className={`${primaryColor} text-white px-5 md:px-6 py-2.5 md:py-3 rounded-xl hover:opacity-90 transition flex items-center gap-2 text-sm md:text-base`}
              >
                <Printer className="w-4 h-4" />
                Print Certificate
              </button>
            )}
            <button 
              onClick={onRestart} 
              className="border-2 border-gray-300 px-5 md:px-6 py-2.5 md:py-3 rounded-xl hover:bg-gray-50 transition flex items-center gap-2 text-sm md:text-base"
            >
              <RotateCw className="w-4 h-4" />
              Restart Quiz
            </button>
          </div>

          {/* Footer */}
          <p className="text-xs text-gray-400 mt-4">
            {passed ? "🏆 Certificate earned! You can print it above." : "Keep learning and try again!"}
          </p>
        </div>
      </div>
    </div>
  );
});

// ============ SUB-COMPONENTS ============

const StatCard = ({ label, value, valueClassName = "", icon }: any) => (
  <div className="bg-gray-50 rounded-xl p-3 md:p-4 border border-gray-100">
    <div className="flex items-center justify-center gap-1 text-xs text-gray-500 mb-1">
      {icon}
      <span>{label}</span>
    </div>
    <h2 className={`text-xl md:text-2xl font-bold ${valueClassName}`}>{value}</h2>
  </div>
);

QuizResults.displayName = "QuizResults";